import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { waiaLogo } from "../../assets";
import { useLocale } from "@/i18n/useLocale";
import LanguageSwitcher from "@/components/LanguageSwitcher/LanguageSwitcher";
import "./Navbar.css";

// Una sola lista para la barra de escritorio y la hoja mobile: asi no pueden
// desincronizarse ni en orden ni en rutas.
const NAV_ITEMS = ["solutions", "enterprise", "cases", "news", "about"] as const;

// Ancho desde el que se ve la barra completa. Tiene que coincidir con el
// media query de Navbar.css: pasado ese ancho la hoja mobile ya no existe y no
// puede quedar abierta (iPad girado, ventana redimensionada).
const DESKTOP_NAV_QUERY = "(min-width: 1280px)";

function Navbar() {
  // La hoja mobile "pertenece" a la navegacion en la que se abrio: cualquier
  // navegacion nueva (link, atras/adelante, cambio de hash) cambia
  // location.key y la cierra sin necesidad de un efecto que haga setState.
  const { key: locationKey } = useLocation();
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  // Atras/adelante cambia la key pero no pasa por ningun handler: sin soltar
  // el valor viejo aca, al volver a esa key la hoja se reabriria sola.
  if (openedAt !== null && openedAt !== locationKey) {
    setOpenedAt(null);
  }
  const isMenuOpen = openedAt === locationKey;
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, path } = useLocale();

  const headerRef = useRef<HTMLElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Mientras la hoja mobile esta abierta se comporta como un panel modal:
  // el foco entra, el resto de la pagina queda inerte y sin scroll, y al
  // cerrar el foco vuelve al boton que la abrio (WCAG 2.4.3, 2.1.2).
  useEffect(() => {
    if (!isMenuOpen) return;

    const toggle = hamburgerRef.current;
    const root = document.documentElement;

    // `inert` en los hermanos de cada ancestro del header: deja afuera del
    // foco y de los lectores de pantalla a todo lo que no es la barra (main,
    // footer, puntos de seccion, WhatsApp, cookies, skip-link) sin depender de
    // los nombres de clase de cada pagina. Con eso el Tab no se escapa a la
    // pagina de atras y no hace falta una trampa de foco manual.
    const inerted: HTMLElement[] = [];
    for (let node: HTMLElement | null = headerRef.current; node && node !== document.body; node = node.parentElement) {
      const parent: HTMLElement | null = node.parentElement;
      if (!parent) break;
      for (const sibling of Array.from(parent.children)) {
        if (sibling !== node && sibling instanceof HTMLElement && !sibling.inert) {
          sibling.inert = true;
          inerted.push(sibling);
        }
      }
    }

    root.classList.add("menu-open");

    // Dos cuadros despues y no en el mismo efecto: con movimiento reducido la
    // transicion de la hoja dura ~0 ms y el navegador todavia la calcula como
    // `visibility: hidden` en este commit, asi que un focus() inmediato no
    // llega al primer link y el foco se queda en el boton.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        menuRef.current?.querySelector<HTMLElement>("a")?.focus({ preventScroll: true });
      });
    });

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenedAt(null);
      }
    };

    const desktop = window.matchMedia(DESKTOP_NAV_QUERY);
    const handleDesktop = (e: MediaQueryListEvent) => {
      if (e.matches) setOpenedAt(null);
    };

    window.addEventListener("keydown", handleKeyDown);
    desktop.addEventListener("change", handleDesktop);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("keydown", handleKeyDown);
      desktop.removeEventListener("change", handleDesktop);
      root.classList.remove("menu-open");
      inerted.forEach((el) => {
        el.inert = false;
      });
      toggle?.focus({ preventScroll: true });
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setOpenedAt(isMenuOpen ? null : locationKey);
  };

  const closeMenu = () => {
    setOpenedAt(null);
  };

  // El ancla de contacto vive en la home del idioma actual. Se arma desde la
  // ruta para que en ingles apunte a /en#contacto y no saque al usuario del
  // sitio en ingles.
  const contactHref = `${path("home")}#contacto`;

  return (
    <header
      ref={headerRef}
      className={`navbar ${isScrolled ? "navbar--scrolled" : ""} ${isMenuOpen ? "navbar--open" : ""}`}
      role="banner"
    >
      <nav className="navbar__container" aria-label={t.nav.mainLabel}>
        <Link to={path("home")} className="navbar__logo" title={`WAIA - ${t.nav.home}`} onClick={closeMenu}>
          <img src={waiaLogo} alt="WAIA" title="WAIA" />
        </Link>

        <div className="navbar__links">
          {NAV_ITEMS.map((key) => (
            <NavLink key={key} to={path(key)} className="navbar__link" title={t.nav[key]} onClick={closeMenu}>
              {t.nav[key]}
            </NavLink>
          ))}
        </div>

        <div className="navbar__actions">
          <LanguageSwitcher onNavigate={closeMenu} />

          <Link to={contactHref} className="navbar__contact-pill" title={t.nav.contact} onClick={closeMenu}>
            {t.nav.contact}
          </Link>
        </div>

        <button
          ref={hamburgerRef}
          type="button"
          className={`navbar__hamburger ${isMenuOpen ? "navbar__hamburger--open" : ""}`}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? t.nav.closeMenu : t.nav.openMenu}
          aria-expanded={isMenuOpen}
          aria-controls="navbar-mobile-menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* inert y no aria-hidden: cerrada, la hoja no puede recibir foco ni
            aparecer en el arbol de accesibilidad, y el navegador no se queja si
            el foco todavia estaba adentro cuando se cierra. */}
        <div
          id="navbar-mobile-menu"
          ref={menuRef}
          className={`navbar__mobile-menu ${isMenuOpen ? "navbar__mobile-menu--open" : ""}`}
          inert={!isMenuOpen}
        >
          <div className="navbar__mobile-links">
            {NAV_ITEMS.map((key) => (
              <NavLink key={key} to={path(key)} className="navbar__mobile-link" title={t.nav[key]} onClick={closeMenu}>
                {t.nav[key]}
              </NavLink>
            ))}

            {/* El pill de escritorio no existe en mobile: sin este boton no
                habria ningun camino al formulario desde el menu. Va abajo,
                al alcance del pulgar, junto al selector de idioma. */}
            <div className="navbar__mobile-footer">
              <Link to={contactHref} className="navbar__mobile-cta" title={t.nav.contact} onClick={closeMenu}>
                {t.nav.contact}
              </Link>

              <div className="navbar__mobile-lang">
                <LanguageSwitcher onNavigate={closeMenu} />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
